import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _get from 'lodash/get';
import _includes from 'lodash/includes';
import _map from 'lodash/map';
import _without from 'lodash/without';
import PropTypes from 'prop-types';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
import withStateReinit from 'hocs/WithStateReinit';
// Core components
import Card from 'components/core/Card';
import Checkbox from 'components/core/Checkbox';
import TextField from 'components/core/TextField';
// Models
import { serialize, usageList } from 'models/cpm/slug';
// Custom
import { errorShape } from 'constants/PropTypesShapes';
import FormDialogTemplate from 'components/templates/FormDialogTemplate';
import { buildFormErrors, slugConstraints } from 'utils/Forms';
import FormHelper from 'components/misc/FormHelper';

class SlugAddEditForm extends React.Component {
  constructor(props) {
    super(props);

    this.handleValidate = this.handleValidate.bind(this);
    this.handleUsagesCheck = this.handleUsagesCheck.bind(this);
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const { onRef } = this.props;
    onRef(this);
  }

  // Handlers called by parent (= DialogItem)
  handleValidate() {
    const {
      fields: {
        domain, comments, slug, usedOnPrintedQrCode, usages,
      },
      onErrorsChange,
      onValidate,
    } = this.props;

    const errors = buildFormErrors(
      {
        domain,
      },
      slugConstraints,
    );

    // Apply errors
    onErrorsChange(errors);

    // No error => validate form
    if (!errors) {
      onValidate(
        serialize({
          domain,
          comments,
          slug,
          usages,
          usedOnPrintedQrCode,
        }),
      );
      return true;
    }
    return false;
  }

  handleUsagesCheck(usage, checked) {
    const {
      fields: { usages },
      onFieldsChange,
    } = this.props;
    const newUsages = checked ? [...usages, usage.value] : _without(usages, usage.value);

    onFieldsChange({ usages: newUsages });
  }

  render() {
    const {
      fields: {
        domain, comments, slug, usedOnPrintedQrCode, usages,
      },
      errors,
      onFieldsChange,
    } = this.props;

    return (
      <FormDialogTemplate errors={errors}>
        <div className="row">
          <div className="col-12">
            {/* ----- DOMAIN ---- */}
            <div className="d-flex">
              <FormHelper
                elements={[
                  {
                    title: <LocalizedMessage id="field.domain" />,
                    content: <LocalizedMessage id="help.slug.domain" />,
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card>
                  <div className="row">
                    <div className="col-12">
                      <TextField
                        margin="none"
                        label={<LocalizedMessage id="field.domain" />}
                        value={domain}
                        required
                        errorText={_get(errors, 'domain')}
                        onChange={(value) => onFieldsChange({
                          domain: value,
                        })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
            {/* ----- SLUG ---- */}
            <div className="d-flex pt-3">
              <FormHelper
                elements={[
                  {
                    title: <LocalizedMessage id="field.slug" />,
                    content: <LocalizedMessage id="help.slug.slug" />,
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card>
                  <div className="row">
                    <div className="col-12 pt-3">
                      <TextField
                        margin="none"
                        label={<LocalizedMessage id="field.slug" />}
                        value={slug}
                        errorText={_get(errors, 'slug')}
                        onChange={(value) => onFieldsChange({
                          slug: value,
                        })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
            {/* ----- USAGES ---- */}
            <div className="d-flex pt-3">
              <FormHelper
                elements={[
                  {
                    title: <LocalizedMessage id="field.slugUsages" />,
                    content: <LocalizedMessage id="help.slug.usageAccounts" />,
                  },
                  {
                    content: <LocalizedMessage id="help.slug.usageUsers" />,
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card>
                  {_map(usageList, (usage) => (
                    <div className="row" key={usage.key}>
                      <div className="col-12">
                        <Checkbox
                          label={<LocalizedMessage id={usage.labelId} />}
                          checked={_includes(usages, usage.value)}
                          onChange={(checked) => this.handleUsagesCheck(usage, checked)}
                        />
                      </div>
                    </div>
                  ))}
                </Card>
              </div>
            </div>
            {/* ----- CHECKBOX USED FOR QR CODE ---- */}
            <div className="d-flex pt-3">
              <FormHelper
                elements={[
                  {
                    title: <LocalizedMessage id="field.usedOnPrintedQrCode" />,
                    content: <LocalizedMessage id="help.slug.usedOnPrintedQrCode" />,
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card>
                  <div className="row">
                    <div className="col-12 pt-3">
                      <Checkbox
                        label={<LocalizedMessage id="field.usedOnPrintedQrCode" />}
                        checked={usedOnPrintedQrCode}
                        onChange={(checked) => onFieldsChange({
                          usedOnPrintedQrCode: checked,
                        })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
            {/* ----- COMMENTS ---- */}
            <div className="d-flex pt-3">
              <FormHelper
                elements={[
                  {
                    title: <LocalizedMessage id="field.comments" />,
                    content: <LocalizedMessage id="help.slug.comments" />,
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card>
                  <div className="row">
                    <div className="col-12 pt-3">
                      <TextField
                        margin="none"
                        label={<LocalizedMessage id="field.comments" />}
                        value={comments}
                        errorText={_get(errors, 'comments')}
                        onChange={(value) => onFieldsChange({
                          comments: value,
                        })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </FormDialogTemplate>
    );
  }
}

SlugAddEditForm.propTypes = {
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onFieldsChange: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
};

SlugAddEditForm.defaultProps = {
  operationError: null,
  errors: null,
};

const mapFieldsToProps = {
  comments: {
    resourceKey: 'comments',
    defaultValue: '',
  },
  domain: {
    resourceKey: 'domain',
    defaultValue: '',
  },
  slug: {
    resourceKey: 'slug',
    defaultValue: '',
  },
  usages: {
    resourceKey: 'usages',
    defaultValue: [],
  },
  usedOnPrintedQrCode: {
    resourceKey: 'usedOnPrintedQrCode',
    defaultValue: false,
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  resourceData: _get(ownProps, 'slug'),
  operationError: {
    ...ownProps.cpmSlugAddError,
    ...ownProps.cpmSlugUpdateError,
  },
});

const mapStateToProps = (state) => ({
  cpmSlugAddError: state.cpmSlug.add.error,
  cpmSlugUpdateError: state.cpmSlug.update.error,
});

module.exports = compose(
  connect(mapStateToProps, null),
  withFormManagement(mapFieldsToProps, mapOwnPropsToResources, false),
  withStateReinit(['updateSlug', 'addSlug']),
)(SlugAddEditForm);



// WEBPACK FOOTER //
// ./routes/cpm/routes/workspaces/assets/slug/SlugAddEditForm.js